import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { AlertOctagon, Loader2, CheckCircle2, Settings2, Clock, Zap, Users } from "lucide-react";
import { useAuth } from "@/lib/auth";

const KIND_ICON = {
  burst: Zap,
  distinct_clients: Users,
  off_hours: Clock,
};

const KIND_LABEL = {
  burst: "Acceso masivo",
  distinct_clients: "Muchos clientes distintos",
  off_hours: "Fuera de horario",
};

/**
 * Admin · Anomalías de acceso.
 * Detecta patrones raros: bursts de lectura, muchos clientes distintos y
 * accesos fuera del horario laboral. Solo tenant_admin/super_admin.
 */
export default function AccessAnomaliesSection() {
  const { user } = useAuth();
  const isAdmin = user && ["tenant_admin", "super_admin"].includes(user.role);
  const [anomalies, setAnomalies] = useState([]);
  const [config, setConfig] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showConfig, setShowConfig] = useState(false);
  const [saving, setSaving] = useState(false);
  const [ackBusy, setAckBusy] = useState(null);

  const load = useCallback(async () => {
    if (!isAdmin) return;
    setLoading(true);
    try {
      const [a, c] = await Promise.all([
        api.get("/admin/access-anomalies?only_open=true&limit=50"),
        api.get("/admin/access-anomaly-config"),
      ]);
      setAnomalies(a.data.anomalies || []);
      setConfig(c.data.config || {});
      setDefaults(c.data.defaults || {});
    } catch (e) {
      if (e?.response?.status !== 403) {
        toast.error(e?.response?.data?.detail || "No se pudo cargar anomalías");
      }
    } finally { setLoading(false); }
  }, [isAdmin]);

  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);

  if (!isAdmin) return null;

  const ack = async (id) => {
    setAckBusy(id);
    try {
      await api.post(`/admin/access-anomalies/${id}/acknowledge`);
      toast.success("Anomalía reconocida");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo reconocer"); }
    finally { setAckBusy(null); }
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      const payload = {};
      for (const k of Object.keys(defaults || {})) {
        if (config?.[k] !== undefined && config?.[k] !== null) payload[k] = config[k];
      }
      await api.put("/admin/access-anomaly-config", payload);
      toast.success("Configuración guardada");
      setShowConfig(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo guardar"); }
    finally { setSaving(false); }
  };

  const setField = (k, v) => setConfig(c => ({ ...c, [k]: v }));

  const numInput = (k, label, help) => (
    <div className="space-y-1" key={k}>
      <label className="overline">{label}</label>
      <input
        type="number"
        value={config?.[k] ?? ""}
        onChange={(e) => setField(k, parseInt(e.target.value) || 0)}
        data-testid={`anomaly-cfg-${k}`}
        className="w-32 bg-background border border-border px-2 py-1 text-sm font-mono focus:border-primary outline-none"
      />
      <p className="text-[10px] text-muted-foreground max-w-[240px]">{help}</p>
    </div>
  );

  return (
    <div className="border border-border p-5 bg-card/50" data-testid="anomaly-section">
      <div className="flex items-start justify-between mb-4 gap-4">
        <div>
          <div className="overline flex items-center gap-2 text-sev-critical">
            <AlertOctagon className="w-3 h-3" /> Anomalías de acceso
          </div>
          <h3 className="font-display text-xl font-medium mt-1">Detección de comportamiento sospechoso</h3>
          <p className="text-xs text-muted-foreground mt-1 max-w-2xl">
            Detecta bursts de lectura, muchos clientes distintos tocados en poco tiempo y accesos fuera del horario laboral.
            Cada anomalía queda registrada y también dispara una alerta estándar.
          </p>
        </div>
        <button
          onClick={() => setShowConfig(s => !s)}
          data-testid="anomaly-config-toggle"
          className="border border-border px-3 py-1.5 text-xs font-mono tracking-widest uppercase hover:border-primary hover:text-primary inline-flex items-center gap-2"
        >
          <Settings2 className="w-3 h-3" /> Umbrales
        </button>
      </div>

      {showConfig && config && defaults && (
        <div className="border border-primary/30 bg-primary/5 p-4 mb-4 space-y-3" data-testid="anomaly-config-panel">
          <div className="overline text-primary">Configuración por tenant</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <label className="flex items-center gap-2 text-xs font-mono col-span-full">
              <input type="checkbox" checked={!!config.enabled}
                     onChange={(e) => setField("enabled", e.target.checked)}
                     data-testid="anomaly-cfg-enabled" />
              Detección habilitada
            </label>
            {numInput("burst_docs_threshold", "Burst · Umbral docs",
                      `Alerta si el usuario lee más de N docs en ${config.burst_window_min || defaults.burst_window_min} min`)}
            {numInput("burst_window_min", "Burst · Ventana (min)",
                      "Ventana temporal para el conteo de burst")}
            {numInput("distinct_clients_threshold", "Clientes distintos · Umbral",
                      `Alerta si el usuario toca más de N clientes distintos en ${config.distinct_window_min || defaults.distinct_window_min} min`)}
            {numInput("distinct_window_min", "Clientes distintos · Ventana (min)", "Ventana temporal")}
            {numInput("off_hours_start", "Off-hours · Inicio (h UTC)",
                      "Hora inclusive donde empieza el bloqueo (22 = 22:00 UTC)")}
            {numInput("off_hours_end", "Off-hours · Fin (h UTC)",
                      "Hora exclusive donde termina el bloqueo (7 = 06:59 UTC)")}
            {numInput("debounce_min", "Debounce (min)",
                      "No re-disparar la misma (usuario, tipo) dentro de N min")}
          </div>
          <label className="flex items-center gap-2 text-xs font-mono">
            <input type="checkbox" checked={!!config.off_hours_enabled}
                   onChange={(e) => setField("off_hours_enabled", e.target.checked)}
                   data-testid="anomaly-cfg-off-hours-enabled" />
            Detectar accesos fuera de horario
          </label>
          <div className="flex gap-2">
            <button onClick={saveConfig} disabled={saving}
                    data-testid="anomaly-cfg-save"
                    className="bg-primary text-primary-foreground px-4 py-2 text-xs font-mono tracking-widest uppercase hover:bg-primary/90 disabled:opacity-50 inline-flex items-center gap-2">
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckCircle2 className="w-3 h-3" />}
              Guardar umbrales
            </button>
            <button onClick={() => setShowConfig(false)}
                    className="px-4 py-2 text-xs text-muted-foreground hover:text-foreground">
              Cerrar
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-xs font-mono text-muted-foreground">
          <Loader2 className="w-3 h-3 animate-spin" /> Cargando anomalías…
        </div>
      ) : anomalies.length === 0 ? (
        <div className="border border-sev-low/30 bg-sev-low/5 p-4 flex items-center gap-2 text-sev-low text-sm" data-testid="anomaly-empty">
          <CheckCircle2 className="w-4 h-4" /> Ninguna anomalía abierta. Todo bajo control.
        </div>
      ) : (
        <div className="space-y-2" data-testid="anomaly-list">
          {anomalies.map(a => {
            const Icon = KIND_ICON[a.kind] || AlertOctagon;
            return (
              <div key={a.id} className="border border-sev-critical/40 bg-sev-critical/5 p-3 flex items-start gap-3" data-testid={`anomaly-row-${a.kind}`}>
                <Icon className="w-4 h-4 text-sev-critical mt-0.5 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] px-1.5 py-0.5 border border-sev-critical/40 text-sev-critical uppercase tracking-widest font-mono">
                      {KIND_LABEL[a.kind] || a.kind}
                    </span>
                    <span className="text-xs font-mono text-muted-foreground">
                      {new Date(a.detected_at).toLocaleString()}
                    </span>
                  </div>
                  <div className="text-sm mt-1">
                    <span className="font-mono font-bold">{a.user_email || a.user_id}</span>
                    {" · "}
                    {a.kind === "burst" && (
                      <span>{a.actual} docs leídos en {a.window_minutes} min (umbral {a.threshold})</span>
                    )}
                    {a.kind === "distinct_clients" && (
                      <span>{a.actual} clientes distintos en {a.window_minutes} min (umbral {a.threshold})</span>
                    )}
                    {a.kind === "off_hours" && (
                      <span>Acceso a las {String(a.actual).padStart(2, "0")}:00 UTC ({a.meta?.window})</span>
                    )}
                  </div>
                </div>
                <button
                  disabled={ackBusy === a.id}
                  onClick={() => ack(a.id)}
                  data-testid={`anomaly-ack-btn-${a.id}`}
                  className="border border-border px-2 py-1 text-[10px] font-mono tracking-widest uppercase hover:border-sev-low hover:text-sev-low disabled:opacity-50 shrink-0"
                >
                  {ackBusy === a.id ? <Loader2 className="w-3 h-3 animate-spin" /> : "Reconocer"}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
